import {
  Avatar,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardProps,
  IconButton,
  List,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Snackbar } from 'src/shared/snackbar/snackbar';
import { SkeletonList } from 'src/shared/skeleton/skeleton-list';
import { generatePath, Link as RouterLink } from 'react-router-dom';
import { APP_PATH } from 'src/config/app-paths';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { generateDataGridRoute } from 'src/feature/data-grid/use-data-grid-search-params/generate-data-grid-route';
import { ChargingSessionListItem } from 'src/shared/charging-session-list-item/charging-session.list-item';
import BoltIcon from '@mui/icons-material/Bolt';
import { useMemo, useState } from 'react';
import { GetChargingSessionsPaginatedModel } from '@delmonicos/shared-sdk/libs/emsp/src/libs/back-office/src/models/charging-sessions/get-charging-session-paginated.model';
import { SortOrder } from '@delmonicos/shared-sdk/libs/emsp/src/libs/core/src/utils/mongo/pagination/models/pagination.model';
import { useGetChargesQuery } from '@service/charging-session.service';

type CustomerChargingSessionListProps = CardProps & {
  pseudo?: string;
};

export function CustomerChargingSessionList(
  props: Readonly<CustomerChargingSessionListProps>,
): React.JSX.Element {
  const { pseudo = '', sx, ...cardProps } = props;
  const { t } = useTranslation();

  // PAGINATION ////////////////////////////////////////////////////////////////

  const [paginationLimit] = useState(3);
  const [paginationOffset] = useState<number>(0);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const getPaginatedQueryParameters = useMemo(() => {
    const params: GetChargingSessionsPaginatedModel = {
      limit: paginationLimit,
      offset: paginationOffset,
      sortOrder: SortOrder.DESC,
      sortField: 'stoppedAt',
      pseudo,
    };

    return params;
  }, [paginationLimit, paginationOffset, pseudo]);

  const { data, isFetching, isError, isUninitialized } = useGetChargesQuery(
    getPaginatedQueryParameters,
    {
      skip: !pseudo,
    },
  );

  return (
    <>
      <Card sx={{ overflow: 'unset', ...sx }} {...cardProps}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: 'primary.light' }}>
              <BoltIcon color="primary" />
            </Avatar>
          }
          title={t('customer.detail.charging-session-section')}
        />

        <CardContent>
          {isFetching || isUninitialized ? (
            <SkeletonList />
          ) : (
            <List>
              {data?.docs.length === 0
                ? t('customer.detail.no-charging-session')
                : data?.docs.map((chargingSession) => (
                    <ChargingSessionListItem
                      key={chargingSession.id}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          component={RouterLink}
                          target="_blank"
                          to={generatePath(APP_PATH.CHARGING_SESSION_DETAIL, {
                            chargingSessionId: chargingSession.id,
                          })}>
                          <OpenInNewIcon />
                        </IconButton>
                      }
                      startedAt={chargingSession.startedAt}
                      city={chargingSession.locationCity}
                      locationName={chargingSession.locationName}
                      totalKwh={chargingSession.energyDelivered}
                    />
                  ))}
            </List>
          )}
        </CardContent>

        {pseudo && data?.hasNextPage && (
          <CardActions>
            <Button
              component={RouterLink}
              target="_blank"
              to={generateDataGridRoute(APP_PATH.LIST_CHARGING_SESSION, {
                filter: { pseudo },
              })}>
              {t('customer.detail.see-more')}
            </Button>
          </CardActions>
        )}
      </Card>

      {isError && (
        <Snackbar
          variant="error"
          message={t('error.get-list-charging-session-query')}
        />
      )}
    </>
  );
}
